/**
 * moon_layer — THE MOON in the sky (2026-09-27, sky/post P2 — realism-additions §1.5): the phase-lit disc and the
 * moonlit sky, added to the ground's background composite (SkyModule) OVER the atmosphere and UNDER the cirrus.
 *
 *   DISC   0.52° (MOON_DISC_DEG), shaded by Lommel-Seeliger — the regolith's law: L ∝ µ₀/(µ₀ + µ), so the FULL
 *          moon is a flat disc with no limb darkening (it looks like a coin, not a ball) and a phase's terminator is
 *          soft; the lit side faces the SUN's true direction (the phase is geometry, never a mask). Seven maria as
 *          analytic dark patches in the disc's own frame (Imbrium, Serenitatis, Tranquillitatis, Crisium,
 *          Procellarum, Nubium, Fecunditatis — the near side's face), −38 % albedo.
 *   SKY    the day sky's own gradient at the moon's elevation, scaled by the moon's top-of-air E0 (a full-moon sky is
 *          the noon sky ×2·10⁻⁶ — a very dark blue), plus the Mie aureole around the disc (HG g 0.7, the air's).
 *
 * Every input is a UNIFORM in scene units the CPU writes each frame (SkyModule): the disc's radiance through
 * `emissiveNits` (capped MOON_DISC_MAX_OVER_KEY_EV over the key — the physical-light contract), the sky's through
 * `luxToScene`. By day the disc is its physical pale self in the blue (the daytime moon); the moonlit sky is then
 * ~10⁻⁶ of the day sky and vanishes on its own.
 *
 * NO-CYCLE RULE (night_live's): SkyModule imports this file, so its uniforms arrive as parameters.
 */
import { float, vec2, vec3, dot, length, max, clamp, mix, smoothstep, sqrt, normalize, cross, fwidth, exp, pow, step, Fn } from 'three/tsl';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface MoonLayerUniforms {
  /** Unit direction to the moon. */
  dir: N;
  /** Unit direction to the sun (the phase's light). */
  sunDir: N;
  /** The disc's radiance at a lit, maria-free point (vec3, scene units). */
  radiance: N;
  /** sin of the disc's angular radius. */
  sinRadius: N;
  /** The moonlit sky at the zenith and the 1° ring (vec3, scene units). */
  skyZenith: N;
  skyHorizon: N;
  /** The aureole's peak (vec3, scene units, the HG lobe normalised to 1 at the disc). */
  aureole: N;
}

/** The near side's seven large maria in the disc's frame (x east-on-the-disc, y toward the zenith; radius 1), with their extent. */
const MARIA: Array<[number, number, number]> = [
  [-0.34, 0.44, 0.27], // Imbrium
  [0.16, 0.38, 0.17], // Serenitatis
  [0.3, 0.08, 0.21], // Tranquillitatis
  [0.72, 0.27, 0.12], // Crisium
  [-0.58, 0.02, 0.33], // Procellarum
  [-0.18, -0.42, 0.18], // Nubium
  [0.55, -0.18, 0.15], // Fecunditatis
];
/** HG with g 0.7, normalised to 1 at θ = 0 (the atmosphere's Mie asymmetry — lib/sky/atmosphere's). */
export const MOON_AUREOLE_G = 0.7;
export function hgLobe(cosT: number, g = MOON_AUREOLE_G): number {
  const peak = (1 - g * g) / Math.pow(1 + g * g - 2 * g, 1.5);
  return ((1 - g * g) / Math.pow(1 + g * g - 2 * g * cosT, 1.5)) / peak;
}

/** The moon's radiance along `viewDir` (disc + moonlit sky + aureole), scene units. */
export function buildMoonLayer(viewDir: N, u: MoonLayerUniforms): N {
  return buildMoonDisc(viewDir, u).add(buildMoonSky(viewDir, u));
}

/**
 * The moonlit sky + the aureole alone (no disc), scene units — split out (the night-sky realism wave, 2026-10-06) so the
 * sky composite can read its luminance: a moonlit sky raises the stars' limiting magnitude and washes the Milky Way
 * through the SAME threshold model as skyglow (lib/sky/night_sky.ts) — the old fixed washes (×1/(1 + 9w) on the photo,
 * ×1/(1 + 2w) on the stars) are gone.
 */
export function buildMoonSky(viewDir: N, u: MoonLayerUniforms): N {
  return Fn(() => {
    const M = u.dir;
    const cosA = dot(viewDir, M);
    const off = viewDir.sub(M.mul(cosA));
    const r = length(off).div(u.sinRadius);
    const inside = float(1).sub(smoothstep(0.98, 1.02, r)).mul(step(0, cosA));
    const y = (viewDir as { y: N }).y;
    const sky = mix(u.skyHorizon, u.skyZenith, sqrt(clamp(y, 0, 1))).mul(smoothstep(-0.06, 0.02, y));
    const g = MOON_AUREOLE_G;
    const hg = float(Math.pow(1 + g * g - 2 * g, 1.5)).mul(pow(float(1 + g * g).sub(cosA.mul(2 * g)), -1.5));
    const aure = u.aureole.mul(hg).mul(float(1).sub(inside)).mul(smoothstep(-0.06, 0.02, y));
    return sky.add(aure);
  })();
}

/** The phase-lit disc alone, scene units. */
export function buildMoonDisc(viewDir: N, u: MoonLayerUniforms): N {
  return Fn(() => {
    const M = u.dir;
    const cosA = dot(viewDir, M);
    // The disc: the view ray's offset from the moon's axis, in units of the disc's radius.
    const off = viewDir.sub(M.mul(cosA));
    const r = length(off).div(u.sinRadius);
    const aa = max(fwidth(r), float(0.02));
    const inside = float(1).sub(smoothstep(float(1).sub(aa), float(1).add(aa), r)).mul(step(0, cosA))
      .mul(smoothstep(-0.012, 0.004, (viewDir as { y: N }).y)); // set behind the horizon line, never through it
    const z = sqrt(max(float(0), float(1).sub(r.mul(r))));
    const nrm = off.div(u.sinRadius).sub(M.mul(z)); // the sphere's normal on the visible face (unit)
    const mu0 = max(dot(nrm, u.sunDir), float(0));
    const ls = mu0.mul(2).div(mu0.add(max(z, float(1e-3)))); // Lommel-Seeliger, 1 over a full disc
    // Maria in the disc's frame: y = the zenith projected on the disc (the face's parallactic turn is ignored).
    const e1 = normalize(cross(vec3(0, 1, 0), M).add(vec3(1e-5, 0, 0)));
    const e2 = cross(M, e1);
    const uv = vec2(dot(off, e1), dot(off, e2)).div(u.sinRadius);
    let maria: N = float(0);
    for (const [mx, my, mr] of MARIA) {
      const d = uv.sub(vec2(mx, my));
      maria = maria.add(exp(dot(d, d).div(-(mr * mr))));
    }
    const albedo = float(1).sub(clamp(maria, 0, 1).mul(0.38));
    return u.radiance.mul(ls).mul(albedo).mul(inside);
  })();
}
